import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import AppDialog from "./AppDialog";

const DialogContext = createContext(null);

export function DialogProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    type: "alert",
    variant: "info",
    icon: "error",
    appName: "sama-tech.ae",
    title: "",
    message: "",
    inputLabel: "",
    inputPlaceholder: "",
    inputType: "text",
    inputValue: "",
    confirmText: "Ok",
    cancelText: "Cancel",
    showCancel: false,
    resolve: null,
  });

  const close = useCallback(() => {
    setState((s) => {
      if (s.resolve) s.resolve(false);
      return { ...s, open: false, resolve: null };
    });
  }, []);

  const alert = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        type: opts?.type || "alert",
        variant: opts?.variant || "info",
        icon: opts?.icon || "error",
        appName: opts?.appName || "sama-tech.ae",
        title: opts?.title || "Notice",
        message: opts?.message || "",
        inputLabel: opts?.inputLabel || "",
        inputPlaceholder: opts?.inputPlaceholder || "",
        inputType: opts?.inputType || "text",
        inputValue: opts?.inputValue || "",
        confirmText: opts?.confirmText || "Ok",
        cancelText: "Cancel",
        showCancel: false,
        resolve,
      });
    });
  }, []);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        type: "confirm",
        variant: opts?.variant || "danger",
        icon: opts?.icon || "trash",
        appName: opts?.appName || "sama-tech.ae",
        title: opts?.title || "Confirm",
        message: opts?.message || "",
        inputLabel: opts?.inputLabel || "",
        inputPlaceholder: opts?.inputPlaceholder || "",
        inputType: opts?.inputType || "text",
        inputValue: opts?.inputValue || "",
        confirmText: opts?.confirmText || "Delete",
        cancelText: opts?.cancelText || "Cancel",
        showCancel: true,
        resolve,
      });
    });
  }, []);

  const onConfirm = useCallback(() => {
    setState((s) => {
      if (s.resolve) s.resolve(s.type === "prompt" ? s.inputValue : true);
      return { ...s, open: false, resolve: null };
    });
  }, []);

  const onInputChange = useCallback((value) => {
    setState((s) => ({ ...s, inputValue: value }));
  }, []);

  const onCancel = useCallback(() => {
    setState((s) => {
      if (s.resolve) s.resolve(false);
      return { ...s, open: false, resolve: null };
    });
  }, []);

  const value = useMemo(() => ({ alert, confirm }), [alert, confirm]);

  return (
    <DialogContext.Provider value={value}>
      {children}
      <AppDialog
        open={state.open}
        type={state.type}
        variant={state.variant}
        icon={state.icon}
        appName={state.appName}
        title={state.title}
        message={state.message}
        inputLabel={state.inputLabel}
        inputPlaceholder={state.inputPlaceholder}
        inputType={state.inputType}
        inputValue={state.inputValue}
        confirmText={state.confirmText}
        cancelText={state.cancelText}
        showCancel={state.showCancel}
        onConfirm={onConfirm}
        onCancel={onCancel}
        onClose={close}
        onInputChange={onInputChange}
      />
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useDialog must be used inside <DialogProvider>");
  return ctx;
}
